// Sort-levels panel — the explicit multi-column sort for the transaction table.
//
// NAMING: the UI calls this "Sort Columns" (team wording, 2026-09-11); the code
// still says "levels" throughout — component names, the CSS module, the pure
// mutators in transactionSortKeys.js. Deliberate: "level" is the precise word
// for an ordered sort key and it keeps `sortKeys[i]` readable, while "column"
// is what a user recognizes. Only visible strings and aria-labels say column.
//
// Design decision (William, 2026-09-10), replacing an earlier click-to-layer
// model: the column-header arrows sort ONE column and toggle its direction,
// full stop. Everything multi-column lives here, in the totals-bar slot that
// already carried the sort feedback.
//
// Exported as two pieces because they sit in different rows of that bar:
//   SortLevelsSummary — the always-visible feedback + disclosure, beside the
//                       Total Transactions / Total Amount stats.
//   SortLevelsEditor  — the expanded level editor, full bar width underneath.
//
// Neither owns state. They render `sortKeys` and report the next value through
// `onChange`, using the pure mutators in transactionSortKeys.js — which is what
// makes the whole interaction unit-testable without rendering the viewer.
import { Box, Button, IconButton, MenuItem, Select, ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

import styles from './SortLevelsPanel.module.css';
import {
  addSortLevel,
  availableSortFields,
  describeSortKeys,
  moveSortLevel,
  removeSortLevel,
  setSortLevelDirection,
  setSortLevelField,
  SORT_FIELD_LABELS,
} from './transactionSortKeys.js';

// Direction labels read in the column's own terms — "Oldest / Newest" beats
// "Ascending" for a date, and an accountant reading a printed report should not
// have to translate.
const directionLabels = (field) => {
  if (field === 'transactionDate') return { asc: 'Oldest', desc: 'Newest' };
  if (field === 'amount') return { asc: 'Low', desc: 'High' };
  return { asc: 'A-Z', desc: 'Z-A' };
};

/**
 * The always-visible half: what the table is sorted by, and the way in.
 * Shown whether the editor is open or closed.
 */
export function SortLevelsSummary({ sortKeys, open, onToggleOpen }) {
  const keys = sortKeys || [];

  return (
    <Box className={styles.root} sx={{ flex: 1 }}>
      <div className={styles.summaryRow}>
        <span className={styles.summaryText}>
          {/* "Sorted by: Unsorted" is nonsense — an empty stack states itself. */}
          {keys.length > 0 && <span className={styles.summaryLabel}>Sorted by: </span>}
          {describeSortKeys(keys)}
        </span>
        <Button
          size="small"
          onClick={onToggleOpen}
          endIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          aria-expanded={open}
        >
          {open ? 'Hide Sort Columns' : 'Sort Columns'}
        </Button>
      </div>

      {keys.length > 1 && !open && (
        <span className={styles.levelCount}>{keys.length} sort columns</span>
      )}
    </Box>
  );
}

/**
 * The expanded editor: order is top-to-bottom, each level carries its own
 * direction control, and promote/demote moves it in the list.
 */
export function SortLevelsEditor({ sortKeys, onChange, onReset, visibleFields, isDefault }) {
  const keys = sortKeys || [];
  const free = availableSortFields(keys, visibleFields);

  return (
    <div className={styles.panel}>
      <div className={styles.panelHead}>
        <span className={styles.panelTitle}>Sort Columns</span>
        <Button size="small" onClick={onReset} disabled={isDefault}>
          Reset Sort
        </Button>
      </div>

      {keys.length === 0 && (
        <div className={styles.empty}>
          No sort — rows are in the order they were loaded.
        </div>
      )}

      {keys.map((key, index) => {
        const labels = directionLabels(key.field);
        // The level's own field stays selectable; the rest of the menu is
        // whatever is unused, so a column can never appear twice.
        const options = [key.field, ...free];

        return (
          <div className={styles.level} key={`${key.field}-${index}`}>
            <span className={styles.levelNumber}>{index + 1}</span>

            <Select
              size="small"
              value={key.field}
              onChange={(e) => onChange(setSortLevelField(keys, index, e.target.value))}
              sx={{ minWidth: 180 }}
              inputProps={{ 'aria-label': `Sort column ${index + 1}` }}
            >
              {options.map((field) => (
                <MenuItem key={field} value={field}>
                  {SORT_FIELD_LABELS[field] || field}
                </MenuItem>
              ))}
            </Select>

            <ToggleButtonGroup
              size="small"
              exclusive
              value={key.direction}
              onChange={(e, value) => {
                // An exclusive group emits null when the active button is
                // re-clicked; a level always has a direction.
                if (value) onChange(setSortLevelDirection(keys, index, value));
              }}
              aria-label={`Sort column ${index + 1} direction`}
            >
              <ToggleButton value="asc">{labels.asc}</ToggleButton>
              <ToggleButton value="desc">{labels.desc}</ToggleButton>
            </ToggleButtonGroup>

            <span className={styles.spacer} />

            <Tooltip title="Move up">
              <span>
                <IconButton
                  size="small"
                  disabled={index === 0}
                  onClick={() => onChange(moveSortLevel(keys, index, -1))}
                  aria-label={`Move sort column ${index + 1} up`}
                >
                  <ArrowUpwardIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Move down">
              <span>
                <IconButton
                  size="small"
                  disabled={index === keys.length - 1}
                  onClick={() => onChange(moveSortLevel(keys, index, 1))}
                  aria-label={`Move sort column ${index + 1} down`}
                >
                  <ArrowDownwardIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Remove column">
              <IconButton
                size="small"
                color="error"
                onClick={() => onChange(removeSortLevel(keys, index))}
                aria-label={`Remove sort column ${index + 1}`}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
        );
      })}

      <div className={styles.footer}>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={() => onChange(addSortLevel(keys, visibleFields))}
          disabled={free.length === 0}
        >
          Add column
        </Button>
        <span className={styles.footerHint}>
          {free.length === 0
            ? 'Every column is already in the sort.'
            : 'Each column breaks ties left by the one above it.'}
        </span>
      </div>
    </div>
  );
}
